import React from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import prism from "../assets/img/prism.webp";
import coq from "../assets/img/coq.png";
import jeune from "../assets/img/jeunelogo.jpg";
import republique from "../assets/img/republique.webp";
import armado from "../assets/img/armado.webp";
import bureau from "../assets/img/bureau.jpg";
import demat from "../assets/img/demat.png";
import { Clock, FileText, Calendar, DollarSign, Users } from "lucide-react";

function AgencyCard({ city, address, type, phone, email, hours }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">{city}</h3>

      <div className="space-y-3">
        <div className="flex items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-blue-600 mr-3 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-gray-700">{address}</span>
        </div>
        <div className="flex items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-blue-600 mr-3 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <span className="text-gray-700">{phone}</span>
        </div>
        <div className="flex items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-blue-600 mr-3 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8h18M4 6h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zm4 4h8v6H8v-6z"
            />
          </svg>
          <span className="text-gray-700">{type}</span>
        </div>
        <div className="flex items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-blue-600 mr-3 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <span className="text-gray-700">{email}</span>
        </div>
        <div className="flex items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-blue-600 mr-3 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-gray-700">{hours}</span>
        </div>
      </div>
      <div className="mt-6"></div>
    </div>
  );
}

export default function APropos() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        {/* About Hero Section */}
        <section className="bg-gradient-to-r from-blue-50 to-indigo-50 pt-[154px] pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                À propos de J'M Intérim
              </h1>
              <p className="text-lg text-gray-700 text-justify">
                Découvrez notre histoire, nos valeurs et notre engagement envers
                l'excellence dans le recrutement temporaire et permanent.
              </p>
            </div>
          </div>
        </section>

        {/* About Us */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Notre histoire
                </h2>
                <p className="text-gray-700 mb-4 text-justify">
                  Fondée en 2007, J'M Intérim est née de la volonté de créer une
                  agence d'intérim à dimension humaine, à l'écoute des besoins
                  des candidats et des entreprises. Nos fondateurs, forts de
                  plus de 15 ans d'expérience dans le secteur du recrutement,
                  ont souhaité développer une approche différente, basée sur la
                  proximité, la réactivité et l'excellence du service.
                </p>
                <p className="text-gray-700 mb-4 text-justify">
                  Au fil des années, J'M Intérim s'est développée pour devenir
                  un acteur reconnu du travail temporaire et du recrutement en
                  France. Notre réseau s'est étendu avec l'ouverture de
                  plusieurs agences, tout en conservant notre philosophie
                  initiale : privilégier la qualité des relations humaines.
                </p>
                <p className="text-gray-700 text-justify">
                  Aujourd'hui, notre entreprise continue de se développer avec
                  toujours le même objectif : être le partenaire privilégié des
                  entreprises et des candidats pour tous leurs besoins en
                  recrutement temporaire et permanent.
                </p>
              </div>
              <div className="relative">
                <img
                  src={bureau}
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-blue-100 rounded-full opacity-50 -z-10"></div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nos valeurs
              </h2>
              <p className="text-lg text-gray-600 text-justify">
                Forts de plus de 15 ans d’expérience dans le milieu du
                recrutement, J'M Intérim allie proactivité et réactivité pour
                offrir les meilleurs services à nos clients et intérimaires.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Proximité humaine
                </h3>
                <p className="text-gray-600 text-justify">
                  Nous privilégions l'écoute, le dialogue et l'accompagnement
                  personnalisé tant auprès des candidats que des entreprises.
                  Chaque personne est unique et mérite une attention
                  particulière.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Confiance et transparence
                </h3>
                <p className="text-gray-600 text-justify">
                  Nous construisons des relations durables basées sur la
                  confiance mutuelle et la transparence. Notre communication est
                  claire, honnête et sans fausse promesse.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Réactivité et efficacité
                </h3>
                <p className="text-gray-600 text-justify">
                  Nous nous engageons à répondre rapidement et efficacement aux
                  besoins de nos clients et candidats, avec un souci constant de
                  qualité et de performance.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                À propos
              </h2>
              <p className="text-lg text-gray-600 text-justify">
                Forts de plus de 15 ans d’expérience dans le milieu du
                recrutement, J'M Intérim allie proactivité et réactivité pour
                offrir les meilleurs services à nos clients et intérimaires.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-lg shadow-sm border border-gray-100 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-circle-user-round-icon lucide-circle-user-round"
                  >
                    <path d="M18 20a6 6 0 0 0-12 0" />
                    <circle cx="12" cy="10" r="4" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Une équipe à taille humaine
                </h3>
                <p className="text-gray-600 text-justify">
                  Proximité, écoute et réactivité pour répondre à chacun de vos
                  besoins. Nous vous accompagnons avec bienveillance et
                  professionnalisme à chaque étape de votre projet. Votre
                  réussite est notre priorité au quotidien.
                  <br></br>
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg shadow-sm border border-gray-100 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Inclusion sociale
                </h3>
                <p className="text-gray-600 text-justify">
                  J'm Intérim s’engage pour l’inclusion sociale des jeunes en
                  situation de handicap. Accessibilité, soutien et opportunités
                  pour révéler votre potentiel.
                  <br></br>
                  <br></br>
                  <div className="flex items-center space-x-4">
                    <a href="https://lesentreprises-sengagent.gouv.fr/les-entreprises-engagees/jm-interim-4621312">
                      <img src={jeune} alt="Logo Jeune" className="w-56" />
                    </a>
                    <a href="https://lesentreprises-sengagent.gouv.fr/les-entreprises-engagees/jm-interim-4621312">
                      <img src={coq} alt="Logo Coq" className="w-56" />
                    </a>
                    <a href="https://www.prismemploi.eu/">
                      <img
                        src={prism}
                        alt="Logo Prism'Emploi"
                        className="w-64"
                      />
                    </a>
                  </div>
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg shadow-sm border border-gray-100 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  0 papier 0 problème
                </h3>
                <p className="text-gray-600 text-justify">
                  Grace à l'outil J'm DémaTT, vous pouvez commander des
                  intérimaires, gérer vos contrats en ligne, saisir vos relevés
                  d'heure, télécharger vos factures, accéder à tous les
                  documents légaux liés à vos recrutements
                  <br></br>
                  <br></br>
                  <div className="flex justify-center">
                    <a href="https://client.jm-interim.com/login">
                      <img
                        src={demat}
                        alt="Logo Prism'Emploi"
                        className="w-40"
                      />
                    </a>
                  </div>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nos agences
              </h2>
              <p className="text-lg text-gray-600">
                Retrouvez nos agences partout en France, pour vous accompagner
                au plus près de chez vous
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <AgencyCard
                city="J'm intérim
- Siège social"
                address="Tour d'Asnières,
4 Av. Laurent Cély Hall A,
92600 Asnières-sur-Seine"
                phone="01.80.88.21.35"
                email="contact@jm-interim.com"
                type="Agence généraliste"
                hours="Lundi - Vendredi : 8h - 18h"
              />

              <AgencyCard
                city="J'm Capeneo Santé
TOULOUSE

"
                address="Carré Wilson,
64 Rue Benjamin Baillaud Bâtiment C, 31500 Toulouse"
                phone="05.61.11.11.00"
                email="sante@jm-interim.com"
                type="Agence généraliste / Specialité : Santé"
                hours="Lundi - Vendredi : 8h - 18h30"
              />

              <AgencyCard
                city="J'm Capeneo
BRIVE"
                address="26 Bis Av. Ribot,
19100 Brive-la-Gaillarde
"
                phone="05.55.17.49.79"
                type="Agence généraliste / Spécialités : BTP, Transport & logistique"
                email="brive@jm-interim.com"
                hours="Lundi - Vendredi : 8h - 18h30"
              />
            </div>

            <div className="mt-12 text-center">
              <Link to="/contact">
                <Button className="bg-blue-600 hover:bg-blue-700 text-lg px-6 py-3 text-white">
                  Contacter une agence
                </Button>{" "}
              </Link>
            </div>
          </div>
        </section>
      </main>{" "}
      <Footer />
    </div>
  );
}
